import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { fecha, num } from '../lib/format';
import {
  FASES_ESTANDAR_LABEL,
  CALIDAD_LABEL,
  type BitacoraLote,
  type BitacoraFase,
  type BitacoraPersonal,
  type BitacoraPeso,
  type Evidencia,
  type FaseEstandar,
  type CalidadCualitativa,
} from '../api/types';
import { PageHeader } from '../components/PageHeader';

const FASES: FaseEstandar[] = [
  'coccion_maiz',
  'remojo',
  'lavado',
  'escurrido',
  'molienda',
  'amasado',
  'armado',
  'asado',
  'reposo',
  'empaque',
  'venta',
];

const CALIDADES: CalidadCualitativa[] = ['ok', 'duro', 'blando', 'chicluda', 'inconsistente'];

export function BitacoraDetallePage() {
  const { id = '' } = useParams();
  const { tiene } = useAuth();
  const puedeEscribir = tiene('bitacora:write');

  const q = useQuery({
    queryKey: ['bitacora', id],
    queryFn: () => API.get<BitacoraLote>(`/bitacora/${id}`),
    enabled: Boolean(id),
  });

  if (q.isLoading) return <div className="text-slate-500">Cargando…</div>;
  if (q.isError || !q.data) return <div className="text-red-600">No se pudo cargar la bitácora.</div>;

  const b = q.data;

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title={b.titulo || `Bitácora ${fecha(b.fecha)}`}
        subtitle={
          <span>
            {fecha(b.fecha)} ·{' '}
            <Link to="/bitacora" className="text-sky-700 hover:underline">
              ← Volver al listado
            </Link>
          </span>
        }
      />

      <Cabecera bitacora={b} puedeEscribir={puedeEscribir} />
      <Fases bitacora={b} puedeEscribir={puedeEscribir} />
      <Personal bitacora={b} puedeEscribir={puedeEscribir} />
      <Pesos bitacora={b} puedeEscribir={puedeEscribir} />
      <Resultados bitacora={b} puedeEscribir={puedeEscribir} />
      <EvidenciaGeneral bitacora={b} puedeEscribir={puedeEscribir} />
    </div>
  );
}

function Section({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section className="card p-4 sm:p-5 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function useActualizarBitacora(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<BitacoraLote>) => API.put<BitacoraLote>(`/bitacora/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bitacora', id] }),
  });
}

function Cabecera({ bitacora, puedeEscribir }: { bitacora: BitacoraLote; puedeEscribir: boolean }) {
  const upd = useActualizarBitacora(bitacora.id);
  const [titulo, setTitulo] = useState(bitacora.titulo ?? '');
  const [objetivo, setObjetivo] = useState(bitacora.objetivoPrueba ?? '');
  const [conclusiones, setConclusiones] = useState(bitacora.conclusiones ?? '');

  const dirty =
    titulo !== (bitacora.titulo ?? '') ||
    objetivo !== (bitacora.objetivoPrueba ?? '') ||
    conclusiones !== (bitacora.conclusiones ?? '');

  return (
    <Section title="Cabecera">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label">Título</label>
          <input
            type="text"
            className="input"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            disabled={!puedeEscribir}
          />
        </div>
        <div>
          <label className="label">Objetivo / hipótesis</label>
          <textarea
            className="input"
            rows={2}
            value={objetivo}
            onChange={(e) => setObjetivo(e.target.value)}
            disabled={!puedeEscribir}
          />
        </div>
        <div className="md:col-span-2">
          <label className="label">Conclusiones / lecciones aprendidas</label>
          <textarea
            className="input"
            rows={3}
            value={conclusiones}
            onChange={(e) => setConclusiones(e.target.value)}
            disabled={!puedeEscribir}
            placeholder="¿Qué aprendiste? ¿Qué ajustarías la próxima vez?"
          />
        </div>
      </div>
      {puedeEscribir && dirty && (
        <button
          type="button"
          className="btn-primary"
          disabled={upd.isPending}
          onClick={() =>
            upd.mutate({
              titulo: titulo || null,
              objetivoPrueba: objetivo || null,
              conclusiones: conclusiones || null,
            })
          }
        >
          {upd.isPending ? 'Guardando…' : 'Guardar cambios'}
        </button>
      )}
    </Section>
  );
}

function Fases({ bitacora, puedeEscribir }: { bitacora: BitacoraLote; puedeEscribir: boolean }) {
  const qc = useQueryClient();
  const [agregar, setAgregar] = useState(false);

  return (
    <Section
      title={`Fases del proceso (${bitacora.fases.length})`}
      action={
        puedeEscribir && (
          <button type="button" className="btn-secondary text-xs" onClick={() => setAgregar(true)}>
            + Añadir fase
          </button>
        )
      }
    >
      {bitacora.fases.length === 0 && !agregar && (
        <p className="text-sm text-slate-500">
          Aún no has registrado fases. Añade la primera (ej. cocción del maíz).
        </p>
      )}

      {bitacora.fases.map((f) => (
        <FaseItem key={f.id} fase={f} bitacoraId={bitacora.id} puedeEscribir={puedeEscribir} evidencias={bitacora.evidencias} />
      ))}

      {agregar && (
        <NuevaFase
          bitacoraId={bitacora.id}
          ordenSiguiente={bitacora.fases.length + 1}
          onListo={() => {
            setAgregar(false);
            qc.invalidateQueries({ queryKey: ['bitacora', bitacora.id] });
          }}
          onCancelar={() => setAgregar(false)}
        />
      )}
    </Section>
  );
}

function FaseItem({
  fase,
  bitacoraId,
  puedeEscribir,
  evidencias,
}: {
  fase: BitacoraFase;
  bitacoraId: string;
  puedeEscribir: boolean;
  evidencias: Evidencia[];
}) {
  const qc = useQueryClient();
  const [editar, setEditar] = useState(false);

  const del = useMutation({
    mutationFn: () => API.del(`/bitacora/fases/${fase.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bitacora', bitacoraId] }),
  });

  const label =
    fase.faseEstandar != null ? FASES_ESTANDAR_LABEL[fase.faseEstandar] : fase.nombrePersonalizado || 'Fase';

  const fotos = evidencias.filter((e) => e.faseId === fase.id);

  if (editar) {
    return (
      <EditarFase
        fase={fase}
        onListo={() => {
          setEditar(false);
          qc.invalidateQueries({ queryKey: ['bitacora', bitacoraId] });
        }}
        onCancelar={() => setEditar(false)}
      />
    );
  }

  return (
    <div className="border border-slate-200 rounded-md p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium">
            {fase.orden}. {label}
          </div>
          <div className="text-xs text-slate-500">
            {formatoHora(fase.horaInicio)} → {formatoHora(fase.horaFin)}
            {fase.responsable && <> · {fase.responsable}</>}
          </div>
        </div>
        {puedeEscribir && (
          <div className="flex gap-1 shrink-0">
            <button className="text-xs text-sky-700 px-2" onClick={() => setEditar(true)}>
              Editar
            </button>
            <button
              className="text-xs text-red-600 px-2"
              onClick={() => {
                if (confirm('¿Eliminar fase?')) del.mutate();
              }}
            >
              Borrar
            </button>
          </div>
        )}
      </div>
      {fase.observaciones && <p className="text-sm text-slate-700">{fase.observaciones}</p>}
      {fase.novedades && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
          ⚠ {fase.novedades}
        </p>
      )}
      <SubirEvidencia bitacoraId={bitacoraId} faseId={fase.id} compacto puedeEscribir={puedeEscribir} />
      {fotos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {fotos.map((e) => (
            <Miniatura key={e.id} evidencia={e} bitacoraId={bitacoraId} puedeEscribir={puedeEscribir} />
          ))}
        </div>
      )}
    </div>
  );
}

function formatoHora(iso: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
}

function isoHoraHoy(valor: string): string | null {
  // valor de <input type="time"> tipo "HH:MM" -> ISO con la fecha de hoy
  if (!valor) return null;
  const [h, m] = valor.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

function horaInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function NuevaFase({
  bitacoraId,
  ordenSiguiente,
  onListo,
  onCancelar,
}: {
  bitacoraId: string;
  ordenSiguiente: number;
  onListo: () => void;
  onCancelar: () => void;
}) {
  const [faseEstandar, setFaseEstandar] = useState<FaseEstandar | ''>('coccion_maiz');
  const [nombrePersonalizado, setNombrePersonalizado] = useState('');
  const [horaInicio, setHoraInicio] = useState('');
  const [horaFin, setHoraFin] = useState('');
  const [responsable, setResponsable] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [novedades, setNovedades] = useState('');

  const crear = useMutation({
    mutationFn: () =>
      API.post(`/bitacora/${bitacoraId}/fases`, {
        faseEstandar: faseEstandar || null,
        nombrePersonalizado: faseEstandar ? null : nombrePersonalizado || null,
        orden: ordenSiguiente,
        horaInicio: isoHoraHoy(horaInicio),
        horaFin: isoHoraHoy(horaFin),
        responsable: responsable || null,
        observaciones: observaciones || null,
        novedades: novedades || null,
      }),
    onSuccess: onListo,
  });

  return (
    <div className="border-2 border-dashed border-sky-300 rounded-md p-3 space-y-2 bg-sky-50/50">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className="label">Fase estándar</label>
          <select
            className="input"
            value={faseEstandar}
            onChange={(e) => setFaseEstandar(e.target.value as FaseEstandar | '')}
          >
            {FASES.map((f) => (
              <option key={f} value={f}>
                {FASES_ESTANDAR_LABEL[f]}
              </option>
            ))}
            <option value="">(personalizada)</option>
          </select>
        </div>
        {!faseEstandar && (
          <div>
            <label className="label">Nombre personalizado</label>
            <input
              type="text"
              className="input"
              value={nombrePersonalizado}
              onChange={(e) => setNombrePersonalizado(e.target.value)}
            />
          </div>
        )}
        <div>
          <label className="label">Hora inicio</label>
          <input type="time" className="input" value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)} />
        </div>
        <div>
          <label className="label">Hora fin</label>
          <input type="time" className="input" value={horaFin} onChange={(e) => setHoraFin(e.target.value)} />
        </div>
        <div>
          <label className="label">Responsable</label>
          <input
            type="text"
            className="input"
            value={responsable}
            onChange={(e) => setResponsable(e.target.value)}
            placeholder="ej. Papá, Milena"
          />
        </div>
      </div>
      <div>
        <label className="label">Observaciones</label>
        <textarea
          className="input"
          rows={2}
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
        />
      </div>
      <div>
        <label className="label">Novedades / problemas</label>
        <textarea
          className="input"
          rows={2}
          value={novedades}
          onChange={(e) => setNovedades(e.target.value)}
          placeholder="ej. la moledora se paró 5 min"
        />
      </div>
      <div className="flex gap-2">
        <button type="button" className="btn-primary" disabled={crear.isPending} onClick={() => crear.mutate()}>
          {crear.isPending ? 'Guardando…' : 'Guardar fase'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

function EditarFase({
  fase,
  onListo,
  onCancelar,
}: {
  fase: BitacoraFase;
  onListo: () => void;
  onCancelar: () => void;
}) {
  const [horaInicio, setHoraInicio] = useState(horaInput(fase.horaInicio));
  const [horaFin, setHoraFin] = useState(horaInput(fase.horaFin));
  const [responsable, setResponsable] = useState(fase.responsable ?? '');
  const [observaciones, setObservaciones] = useState(fase.observaciones ?? '');
  const [novedades, setNovedades] = useState(fase.novedades ?? '');

  const upd = useMutation({
    mutationFn: () =>
      API.put(`/bitacora/fases/${fase.id}`, {
        horaInicio: isoHoraHoy(horaInicio),
        horaFin: isoHoraHoy(horaFin),
        responsable: responsable || null,
        observaciones: observaciones || null,
        novedades: novedades || null,
      }),
    onSuccess: onListo,
  });

  return (
    <div className="border-2 border-sky-400 rounded-md p-3 space-y-2 bg-sky-50/50">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div>
          <label className="label">Hora inicio</label>
          <input type="time" className="input" value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)} />
        </div>
        <div>
          <label className="label">Hora fin</label>
          <input type="time" className="input" value={horaFin} onChange={(e) => setHoraFin(e.target.value)} />
        </div>
        <div>
          <label className="label">Responsable</label>
          <input type="text" className="input" value={responsable} onChange={(e) => setResponsable(e.target.value)} />
        </div>
      </div>
      <div>
        <label className="label">Observaciones</label>
        <textarea className="input" rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
      </div>
      <div>
        <label className="label">Novedades</label>
        <textarea className="input" rows={2} value={novedades} onChange={(e) => setNovedades(e.target.value)} />
      </div>
      <div className="flex gap-2">
        <button type="button" className="btn-primary" disabled={upd.isPending} onClick={() => upd.mutate()}>
          {upd.isPending ? 'Guardando…' : 'Guardar'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

function Personal({ bitacora, puedeEscribir }: { bitacora: BitacoraLote; puedeEscribir: boolean }) {
  const qc = useQueryClient();
  const [nombre, setNombre] = useState('');
  const [horaEntrada, setHoraEntrada] = useState('');
  const [rol, setRol] = useState('');

  const add = useMutation({
    mutationFn: () =>
      API.post(`/bitacora/${bitacora.id}/personal`, {
        nombre,
        horaEntrada: isoHoraHoy(horaEntrada),
        rol: rol || null,
      }),
    onSuccess: () => {
      setNombre('');
      setHoraEntrada('');
      setRol('');
      qc.invalidateQueries({ queryKey: ['bitacora', bitacora.id] });
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => API.del(`/bitacora/personal/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bitacora', bitacora.id] }),
  });

  return (
    <Section title={`Personal (${bitacora.personal.length})`}>
      <div className="space-y-2">
        {bitacora.personal.map((p: BitacoraPersonal) => (
          <div key={p.id} className="flex items-center justify-between gap-2 text-sm bg-slate-50 border border-slate-200 rounded p-2">
            <span>
              <b>{p.nombre}</b>
              {p.rol && <span className="text-slate-500"> · {p.rol}</span>}
              {p.horaEntrada && (
                <span className="text-slate-500 ml-2">entró {formatoHora(p.horaEntrada)}</span>
              )}
            </span>
            {puedeEscribir && (
              <button className="text-xs text-red-600 px-2" onClick={() => del.mutate(p.id)}>
                ×
              </button>
            )}
          </div>
        ))}
      </div>
      {puedeEscribir && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end pt-2 border-t border-slate-100">
          <div>
            <label className="label">Nombre</label>
            <input
              type="text"
              className="input"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="ej. Milena"
            />
          </div>
          <div>
            <label className="label">Hora entrada</label>
            <input type="time" className="input" value={horaEntrada} onChange={(e) => setHoraEntrada(e.target.value)} />
          </div>
          <div>
            <label className="label">Rol (opcional)</label>
            <input
              type="text"
              className="input"
              value={rol}
              onChange={(e) => setRol(e.target.value)}
              placeholder="ej. asador"
            />
          </div>
          <button
            type="button"
            className="btn-secondary"
            disabled={!nombre || add.isPending}
            onClick={() => add.mutate()}
          >
            + Añadir
          </button>
        </div>
      )}
    </Section>
  );
}

function Pesos({ bitacora, puedeEscribir }: { bitacora: BitacoraLote; puedeEscribir: boolean }) {
  const qc = useQueryClient();
  const [peso, setPeso] = useState('');
  const [momento, setMomento] = useState('antes_asar');

  const add = useMutation({
    mutationFn: () =>
      API.post(`/bitacora/${bitacora.id}/pesos`, {
        orden: bitacora.pesos.length + 1,
        pesoG: Number(peso),
        momento,
      }),
    onSuccess: () => {
      setPeso('');
      qc.invalidateQueries({ queryKey: ['bitacora', bitacora.id] });
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => API.del(`/bitacora/pesos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bitacora', bitacora.id] }),
  });

  const pesos = bitacora.pesos.map((p) => Number(p.pesoG));
  const prom = pesos.length ? pesos.reduce((a, b) => a + b, 0) / pesos.length : 0;
  const min = pesos.length ? Math.min(...pesos) : 0;
  const max = pesos.length ? Math.max(...pesos) : 0;

  return (
    <Section title={`Pesos de muestra (${bitacora.pesos.length})`}>
      {bitacora.pesos.length > 0 && (
        <div className="text-xs text-slate-600 flex flex-wrap gap-x-4">
          <span>Promedio: <b>{num(prom, 1)} g</b></span>
          <span>Mín: {num(min, 1)} g</span>
          <span>Máx: {num(max, 1)} g</span>
          <span>Rango: {num(max - min, 1)} g</span>
        </div>
      )}
      <div className="space-y-1">
        {bitacora.pesos.map((p: BitacoraPeso) => (
          <div key={p.id} className="flex items-center justify-between gap-2 text-sm bg-slate-50 border border-slate-200 rounded px-2 py-1">
            <span>
              #{p.orden} · <b>{num(Number(p.pesoG), 1)} g</b>
              {p.momento && <span className="text-slate-500 ml-2 text-xs">{p.momento}</span>}
            </span>
            {puedeEscribir && (
              <button className="text-xs text-red-600 px-2" onClick={() => del.mutate(p.id)}>
                ×
              </button>
            )}
          </div>
        ))}
      </div>
      {puedeEscribir && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end pt-2 border-t border-slate-100">
          <div>
            <label className="label">Peso (g)</label>
            <input
              type="number"
              step="0.1"
              className="input"
              value={peso}
              onChange={(e) => setPeso(e.target.value)}
              placeholder="ej. 164"
            />
          </div>
          <div>
            <label className="label">Momento</label>
            <select className="input" value={momento} onChange={(e) => setMomento(e.target.value)}>
              <option value="antes_asar">Antes de asar</option>
              <option value="despues_asar">Después de asar</option>
            </select>
          </div>
          <button
            type="button"
            className="btn-secondary"
            disabled={!peso || add.isPending}
            onClick={() => add.mutate()}
          >
            + Añadir peso
          </button>
        </div>
      )}
    </Section>
  );
}

function Resultados({ bitacora, puedeEscribir }: { bitacora: BitacoraLote; puedeEscribir: boolean }) {
  const upd = useActualizarBitacora(bitacora.id);
  const [arepasDefectuosas, setArepasDefectuosas] = useState(bitacora.arepasDefectuosas);
  const [arepasMuestra, setArepasMuestra] = useState(bitacora.arepasMuestra);
  const [arepasVendidas, setArepasVendidas] = useState(bitacora.arepasVendidas);
  const [calidadMasa, setCalidadMasa] = useState<CalidadCualitativa | ''>(bitacora.calidadMasa ?? '');
  const [calidadCoccion, setCalidadCoccion] = useState<CalidadCualitativa | ''>(bitacora.calidadCoccion ?? '');

  const dirty =
    arepasDefectuosas !== bitacora.arepasDefectuosas ||
    arepasMuestra !== bitacora.arepasMuestra ||
    arepasVendidas !== bitacora.arepasVendidas ||
    calidadMasa !== (bitacora.calidadMasa ?? '') ||
    calidadCoccion !== (bitacora.calidadCoccion ?? '');

  return (
    <Section title="Resultados del lote">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div>
          <label className="label">Defectuosas</label>
          <input
            type="number"
            className="input"
            value={arepasDefectuosas}
            onChange={(e) => setArepasDefectuosas(Number(e.target.value))}
            disabled={!puedeEscribir}
          />
        </div>
        <div>
          <label className="label">Muestra</label>
          <input
            type="number"
            className="input"
            value={arepasMuestra}
            onChange={(e) => setArepasMuestra(Number(e.target.value))}
            disabled={!puedeEscribir}
          />
        </div>
        <div>
          <label className="label">Vendidas</label>
          <input
            type="number"
            className="input"
            value={arepasVendidas}
            onChange={(e) => setArepasVendidas(Number(e.target.value))}
            disabled={!puedeEscribir}
          />
        </div>
        <div>
          <label className="label">Calidad masa</label>
          <select
            className="input"
            value={calidadMasa}
            onChange={(e) => setCalidadMasa(e.target.value as CalidadCualitativa | '')}
            disabled={!puedeEscribir}
          >
            <option value="">—</option>
            {CALIDADES.map((c) => (
              <option key={c} value={c}>
                {CALIDAD_LABEL[c]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Cocción maíz</label>
          <select
            className="input"
            value={calidadCoccion}
            onChange={(e) => setCalidadCoccion(e.target.value as CalidadCualitativa | '')}
            disabled={!puedeEscribir}
          >
            <option value="">—</option>
            {CALIDADES.map((c) => (
              <option key={c} value={c}>
                {CALIDAD_LABEL[c]}
              </option>
            ))}
          </select>
        </div>
      </div>
      {puedeEscribir && dirty && (
        <button
          type="button"
          className="btn-primary"
          disabled={upd.isPending}
          onClick={() =>
            upd.mutate({
              arepasDefectuosas,
              arepasMuestra,
              arepasVendidas,
              calidadMasa: calidadMasa || null,
              calidadCoccion: calidadCoccion || null,
            })
          }
        >
          {upd.isPending ? 'Guardando…' : 'Guardar resultados'}
        </button>
      )}
    </Section>
  );
}

function EvidenciaGeneral({ bitacora, puedeEscribir }: { bitacora: BitacoraLote; puedeEscribir: boolean }) {
  const generales = bitacora.evidencias.filter((e) => !e.faseId);

  return (
    <Section title={`Evidencia general (${generales.length})`}>
      <SubirEvidencia bitacoraId={bitacora.id} puedeEscribir={puedeEscribir} />
      {generales.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 pt-2">
          {generales.map((e) => (
            <Miniatura key={e.id} evidencia={e} bitacoraId={bitacora.id} puedeEscribir={puedeEscribir} />
          ))}
        </div>
      )}
    </Section>
  );
}

function SubirEvidencia({
  bitacoraId,
  faseId,
  compacto,
  puedeEscribir,
}: {
  bitacoraId: string;
  faseId?: string;
  compacto?: boolean;
  puedeEscribir: boolean;
}) {
  const qc = useQueryClient();
  const [subiendo, setSubiendo] = useState(false);

  if (!puedeEscribir) return null;

  async function onChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    setSubiendo(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (faseId) form.append('faseId', faseId);
      await API.upload(`/bitacora/${bitacoraId}/evidencias`, form);
      qc.invalidateQueries({ queryKey: ['bitacora', bitacoraId] });
    } catch (err: unknown) {
      const e = err as { message?: string };
      alert(`No se pudo subir: ${e.message ?? 'error'}`);
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <label
      className={`inline-flex items-center gap-2 cursor-pointer ${
        compacto ? 'text-xs text-sky-700' : 'btn-secondary'
      }`}
    >
      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onChange} disabled={subiendo} />
      {subiendo ? 'Subiendo…' : compacto ? '📷 Añadir foto a esta fase' : '📷 Subir foto'}
    </label>
  );
}

function Miniatura({
  evidencia,
  bitacoraId,
  puedeEscribir,
}: {
  evidencia: Evidencia;
  bitacoraId: string;
  puedeEscribir: boolean;
}) {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => API.del(`/bitacora/evidencias/${evidencia.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bitacora', bitacoraId] }),
  });

  return (
    <div className="relative group">
      <a href={evidencia.url} target="_blank" rel="noopener noreferrer">
        <img
          src={evidencia.url}
          alt={evidencia.descripcion ?? 'Evidencia'}
          className="w-full aspect-square object-cover rounded border border-slate-200"
          loading="lazy"
        />
      </a>
      {puedeEscribir && (
        <button
          type="button"
          className="absolute top-1 right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => {
            if (confirm('¿Eliminar esta foto?')) del.mutate();
          }}
          title="Eliminar"
        >
          ×
        </button>
      )}
    </div>
  );
}
